import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Printer, RefreshCw, CheckCircle, AlertCircle, Building2, Users, X, ChevronLeft } from 'lucide-react';
import thermalPrinter from '../services/ThermalPrinterService';

// Get API URL without auth requirement
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Kiosko ID could be read from a query string in the future; for now we
// hardcode kiosko-1. The config is fetched per-kiosk so adding a new
// physical kiosk only requires changing this constant in that build.
const KIOSKO_ID = 'kiosko-1';

// Format Chilean RUT progressively as the user types: 12.345.678-9
const formatRutInput = (raw) => {
  const clean = String(raw || '').replace(/[^0-9kK]/g, '').toUpperCase().slice(0, 9);
  if (clean.length <= 1) return clean;
  const dv = clean.slice(-1);
  const body = clean.slice(0, -1);
  // Add thousands separator (dots)
  const bodyWithDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${bodyWithDots}-${dv}`;
};

const KioskPage = () => {
  const [turno, setTurno] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [printerStatus, setPrinterStatus] = useState('checking'); // checking, ready, unavailable
  const [autoPrint, setAutoPrint] = useState(true); // Auto-print when taking a number
  const [boxEmpresa, setBoxEmpresa] = useState(null); // Box assigned to empresa
  const [kioskoConfig, setKioskoConfig] = useState(null);
  // step: 'BUTTONS' (choose normal/empresa) | 'RUT' (capture RUT first)
  const [step, setStep] = useState('BUTTONS');
  const [rutInput, setRutInput] = useState('');
  const [pendingTipo, setPendingTipo] = useState(null); // 'normal' | 'empresa' while in RUT step

  const checkBoxEmpresa = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/puntos-atencion?activos_only=true`);
      // Find ACTIVE box with name containing "empresa"
      const empresaBox = response.data.find(p => 
        p.nombre.toLowerCase().includes('empresa') && p.activo
      );
      setBoxEmpresa(empresaBox);
    } catch (err) {
      console.error('Error checking empresa box:', err);
    }
  }, []);

  const loadKioskoConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kiosko/config/${KIOSKO_ID}`);
      setKioskoConfig(r.data);
    } catch (err) {
      console.error('Error loading kiosko config:', err);
    }
  }, []);

  // Check thermal printer availability and empresa box on mount
  useEffect(() => {
    checkPrinter();
    checkBoxEmpresa();
    loadKioskoConfig();
  }, [checkBoxEmpresa, loadKioskoConfig]);

  // WebSocket for real-time box updates
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/kiosk'
      : `ws://${window.location.host}/api/ws/kiosk`;
    
    let ws = null;
    let reconnectTimeout = null;
    
    const connect = () => {
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket kiosk connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('WS kiosk message:', message.type);
          
          if (message.type === 'BOX_UPDATE') {
            // Boxes were updated - refresh empresa box status
            const boxes = message.data;
            const empresaBox = boxes.find(p => 
              p.nombre.toLowerCase().includes('empresa') && p.activo
            );
            setBoxEmpresa(empresaBox);
            console.log('Box empresa updated:', empresaBox ? empresaBox.nombre : 'none');
          }
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket kiosk disconnected, reconnecting...');
        reconnectTimeout = setTimeout(connect, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket kiosk error:', error);
      };
    };
    
    connect();
    
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, []);

  const checkPrinter = async () => {
    setPrinterStatus('checking');
    try {
      const available = await thermalPrinter.isAvailable();
      if (available) {
        const printer = await thermalPrinter.findDefaultPrinter();
        setPrinterStatus(printer ? 'ready' : 'unavailable');
      } else {
        setPrinterStatus('unavailable');
      }
    } catch {
      setPrinterStatus('unavailable');
    }
  };

  // Initiate the flow when a "Empresa" / "General" button is pressed.
  // When kiosko config has rut_input_enabled === true we move to the RUT step;
  // otherwise take the turn directly (legacy behavior preserved).
  const handlePickTipo = (tipo) => {
    if (kioskoConfig?.rut_input_enabled) {
      setPendingTipo(tipo);
      setRutInput('');
      setError(null);
      setStep('RUT');
      return;
    }
    tomarTurno(tipo);
  };

  // Take the turn. Sends optional rut + kiosko_id to backend.
  const tomarTurno = async (tipo = 'normal', rutOverride = null) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = tipo === 'empresa' ? `${API}/turnos/tomar-empresa` : `${API}/turnos/tomar`;
      const rutToSend = rutOverride !== null ? rutOverride : (kioskoConfig?.rut_input_enabled ? rutInput.trim() : '');
      const payload = {
        kiosko_id: KIOSKO_ID,
        ...(rutToSend ? { rut: rutToSend } : {}),
      };
      const response = await axios.post(endpoint, payload);
      setTurno({ ...response.data, tipo });
      setStep('BUTTONS');
      setRutInput('');
      setPendingTipo(null);

      if (autoPrint && printerStatus === 'ready') {
        setTimeout(() => imprimirTermico(response.data, tipo), 500);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Error al tomar turno. Intente nuevamente.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Validate RUT format before submitting (chilean: digits + optional K)
  const isRutValid = (rut) => {
    const clean = String(rut || '').replace(/[^0-9kK]/g, '');
    return clean.length >= 8; // 7 digits body + 1 DV minimum
  };

  const handleSubmitRut = () => {
    if (!pendingTipo) return;
    const trimmed = rutInput.trim();
    if (!trimmed && kioskoConfig?.rut_required) {
      setError('Debes ingresar un RUT para continuar');
      return;
    }
    if (trimmed && !isRutValid(trimmed)) {
      setError('RUT inválido. Verifica el formato (ej: 12.345.678-9)');
      return;
    }
    tomarTurno(pendingTipo, trimmed);
  };

  const handleOmitRut = () => {
    if (!pendingTipo) return;
    tomarTurno(pendingTipo, '');
  };

  const handleCancelRut = () => {
    setStep('BUTTONS');
    setRutInput('');
    setPendingTipo(null);
    setError(null);
  };

  // Numeric keypad button handler
  const appendDigit = (d) => {
    setRutInput(prev => formatRutInput((prev + d)));
  };
  const removeLastChar = () => {
    setRutInput(prev => {
      const clean = String(prev || '').replace(/[^0-9kK]/g, '');
      return formatRutInput(clean.slice(0, -1));
    });
  };

  // Thermal printer print function
  const imprimirTermico = async (turnoData = null, tipo = 'normal') => {
    const turnoToPrint = turnoData || turno;
    if (!turnoToPrint) return;
    
    setPrinting(true);
    try {
      const esEmpresaActivo = turnoToPrint.punto_empresa_activo;
      await thermalPrinter.printTicket(turnoToPrint, {
        titulo: 'VACU-TRACK',
        subtitulo: tipo === 'empresa' ? 'TURNO EMPRESA' : 'by ExtremeBits',
        mensaje: tipo === 'empresa' && esEmpresaActivo
          ? `Diríjase a ${turnoToPrint.punto_empresa_nombre || 'Box Empresa'}`
          : 'Por favor espere a ser llamado',
        footer: 'Gracias por su preferencia'
      });
    } catch (err) {
      console.error('Error printing:', err);
      // Fallback to browser print
      imprimirNavegador();
    } finally {
      setPrinting(false);
    }
  };

  // Browser fallback print function
  const imprimirNavegador = () => {
    if (!turno) return;

    const esEmpresa = turno.tipo === 'empresa';
    const esEmpresaActivo = turno.punto_empresa_activo;

    // Escape any value that ends up in HTML to prevent XSS via injected ticket data.
    const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (m) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]
    ));

    const numero = escapeHtml(turno.numero_formateado);
    const empresaNombre = escapeHtml(turno.punto_empresa_nombre || 'Box Empresa');
    const fecha = new Date().toLocaleDateString('es-CL', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
    const subtitle = esEmpresa ? 'TURNO EMPRESA' : 'by ExtremeBits';
    const subColor = esEmpresa ? '#059669' : '#666';
    const subWeight = esEmpresa ? 'bold' : 'normal';
    const numColor = esEmpresa ? '#059669' : '#000';
    const mensaje = esEmpresa && esEmpresaActivo
      ? `Diríjase a ${empresaNombre}`
      : 'Por favor espere a ser llamado';

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Turno ${numero}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    body { font-family: 'Courier New', monospace; text-align: center; padding: 10px; width: 76mm; margin: 0 auto; }
    .logo { font-size: 16px; font-weight: bold; margin-bottom: 5px; }
    .subtitle { font-size: 10px; color: ${subColor}; margin-bottom: 15px; font-weight: ${subWeight}; }
    .numero { font-size: 48px; font-weight: bold; margin: 15px 0; letter-spacing: 2px; color: ${numColor}; }
    .info { font-size: 12px; color: #333; margin: 8px 0; }
    .fecha { font-size: 10px; color: #666; margin-top: 15px; }
    .linea { border-top: 1px dashed #000; margin: 10px 0; }
    .mensaje { font-size: 10px; color: #666; margin-top: 8px; }
    .empresa-badge { background: #059669; color: white; padding: 4px 8px; border-radius: 4px; font-size: 10px; display: inline-block; margin-bottom: 10px; }
  </style>
</head>
<body>
  <div class="logo">VACU-TRACK</div>
  <div class="subtitle">${escapeHtml(subtitle)}</div>
  ${esEmpresa ? '<div class="empresa-badge">EMPRESA</div>' : ''}
  <div class="linea"></div>
  <div class="info">SU TURNO ES</div>
  <div class="numero">${numero}</div>
  <div class="linea"></div>
  <div class="fecha">${escapeHtml(fecha)}</div>
  <div class="mensaje">${mensaje}</div>
  <div class="mensaje">Gracias por su preferencia</div>
</body>
</html>`;

    // Use a Blob URL instead of document.write (which is unsafe and deprecated).
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank', 'width=400,height=600');
    if (!printWindow) {
      URL.revokeObjectURL(url);
      return;
    }
    printWindow.addEventListener('load', () => {
      try {
        printWindow.print();
        // Some browsers close before print returns; guard with setTimeout
        setTimeout(() => {
          try { printWindow.close(); } catch (e) { console.debug('printWindow.close failed:', e); }
        }, 500);
      } catch (e) {
        console.warn('Auto-print failed, user can press Ctrl+P:', e);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    });
  };

  const imprimirTurno = () => {
    if (printerStatus === 'ready') {
      imprimirTermico();
    } else {
      imprimirNavegador();
    }
  };

  const resetear = () => {
    setTurno(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center text-white mb-8">
          <h1 className="text-4xl font-bold mb-2">Vacu-Track</h1>
          <p className="text-blue-200">by ExtremeBits</p>
        </div>

        {/* Printer Status Indicator */}
        <div className="flex justify-center mb-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
            printerStatus === 'ready' 
              ? 'bg-emerald-500/20 text-emerald-200' 
              : printerStatus === 'checking'
              ? 'bg-blue-500/20 text-blue-200'
              : 'bg-amber-500/20 text-amber-200'
          }`}>
            {printerStatus === 'ready' && (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Impresora térmica lista</span>
              </>
            )}
            {printerStatus === 'checking' && (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Verificando impresora...</span>
              </>
            )}
            {printerStatus === 'unavailable' && (
              <>
                <AlertCircle className="w-4 h-4" />
                <span>Impresión por navegador</span>
              </>
            )}
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {!turno && step === 'RUT' && (
            // RUT capture screen (only when kiosko.rut_input_enabled === true)
            <div className="p-8 text-center">
              <button
                onClick={handleCancelRut}
                className="absolute top-6 left-6 p-2 rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Volver"
                data-testid="btn-cancel-rut"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Ingresa tu RUT
              </h2>
              <p className="text-slate-500 mb-6 text-sm">
                {kioskoConfig?.rut_required
                  ? 'Es obligatorio para continuar'
                  : 'Es opcional. Puedes omitir este paso.'}
              </p>

              {error && (
                <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-sm">
                  {error}
                </div>
              )}

              <div className="relative mb-6">
                <input
                  type="text"
                  value={rutInput}
                  onChange={(e) => setRutInput(formatRutInput(e.target.value))}
                  placeholder="12.345.678-9"
                  className="w-full text-center text-4xl font-mono font-bold py-5 px-6 bg-slate-50 border-2 border-slate-200 rounded-2xl focus:border-blue-500 focus:outline-none tracking-wider"
                  data-testid="input-rut-kiosko"
                  autoFocus
                  inputMode="none"
                  readOnly
                />
                {rutInput && (
                  <button
                    onClick={() => setRutInput('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600"
                    aria-label="Limpiar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* On-screen numeric keypad */}
              <div className="grid grid-cols-3 gap-3 mb-6 max-w-sm mx-auto">
                {['1','2','3','4','5','6','7','8','9'].map(d => (
                  <button
                    key={d}
                    onClick={() => appendDigit(d)}
                    className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-900 text-3xl font-bold py-5 rounded-2xl transition-colors"
                    data-testid={`keypad-${d}`}
                  >
                    {d}
                  </button>
                ))}
                <button
                  onClick={() => appendDigit('K')}
                  className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-900 text-3xl font-bold py-5 rounded-2xl transition-colors"
                  data-testid="keypad-K"
                >
                  K
                </button>
                <button
                  onClick={() => appendDigit('0')}
                  className="bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-900 text-3xl font-bold py-5 rounded-2xl transition-colors"
                  data-testid="keypad-0"
                >
                  0
                </button>
                <button
                  onClick={removeLastChar}
                  className="bg-amber-100 hover:bg-amber-200 active:bg-amber-300 text-amber-700 text-xl font-bold py-5 rounded-2xl transition-colors"
                  data-testid="keypad-back"
                  aria-label="Borrar"
                >
                  ←
                </button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleSubmitRut}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xl font-bold py-5 px-8 rounded-2xl transition-all active:scale-95 disabled:opacity-50 shadow-lg"
                  data-testid="btn-rut-continuar"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Generando turno...
                    </span>
                  ) : 'Continuar'}
                </button>
                {!kioskoConfig?.rut_required && (
                  <button
                    onClick={handleOmitRut}
                    disabled={loading}
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-lg font-semibold py-4 px-8 rounded-2xl transition-colors disabled:opacity-50"
                    data-testid="btn-rut-omitir"
                  >
                    Omitir y tomar turno sin RUT
                  </button>
                )}
              </div>
            </div>
          )}

          {!turno && step !== 'RUT' && (
            // Take number screen
            <div className="p-8 text-center">
              <h2 className="text-2xl font-bold text-slate-800 mb-4">
                Bienvenido
              </h2>
              <p className="text-slate-600 mb-8">
                Seleccione el tipo de atención que necesita
              </p>
              
              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Normal Ticket Button */}
                {kioskoConfig?.allow_general !== false && (
                <button
                  onClick={() => handlePickTipo('normal')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xl font-bold py-6 px-8 rounded-2xl transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-3"
                  data-testid="btn-tomar-turno"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <RefreshCw className="w-6 h-6 animate-spin" />
                      Obteniendo turno...
                    </span>
                  ) : (
                    <>
                      <Users className="w-8 h-8" />
                      TURNO GENERAL
                    </>
                  )}
                </button>
                )}

                {/* Empresa Ticket Button - Only show if there's an empresa box configured */}
                {boxEmpresa && kioskoConfig?.allow_empresa !== false && (
                  <button
                    onClick={() => handlePickTipo('empresa')}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xl font-bold py-6 px-8 rounded-2xl transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-3"
                    data-testid="btn-tomar-turno-empresa"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <RefreshCw className="w-6 h-6 animate-spin" />
                        Obteniendo turno...
                      </span>
                    ) : (
                      <>
                        <Building2 className="w-8 h-8" />
                        TURNO EMPRESA
                      </>
                    )}
                  </button>
                )}
              </div>

              <p className="text-sm text-slate-500 mt-6">
                {printerStatus === 'ready' 
                  ? 'Su ticket se imprimirá automáticamente'
                  : 'Espere a que su número sea llamado en pantalla'
                }
              </p>
              
              {boxEmpresa && (
                <p className="text-xs text-emerald-600 mt-2">
                  Turno Empresa: Atención prioritaria en {boxEmpresa.nombre}
                </p>
              )}
            </div>
          )}

          {turno && (
            // Ticket display screen
            <div className="p-8 text-center">
              <div className={`${turno.tipo === 'empresa' ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-100 text-emerald-700'} p-3 rounded-full inline-block mb-4`}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {turno.tipo === 'empresa' && (
                <div className="bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-medium inline-block mb-3">
                  TURNO EMPRESA - PRIORIDAD
                </div>
              )}
              
              <h2 className="text-xl font-semibold text-slate-800 mb-2">
                Su número de turno es
              </h2>
              
              <div className="my-8">
                <div className={`text-8xl font-bold ${turno.tipo === 'empresa' ? 'text-emerald-600' : 'text-blue-600'} mb-2`}>
                  {turno.numero_formateado}
                </div>
                <p className="text-slate-500">
                  {new Date().toLocaleDateString('es-CL', { 
                    weekday: 'long', 
                    day: 'numeric',
                    month: 'long'
                  })}
                </p>
              </div>

              <div className={`${turno.tipo === 'empresa' ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-100'} rounded-xl p-4 mb-6`}>
                <p className="text-slate-600">
                  {turno.tipo === 'empresa' && turno.punto_empresa_activo
                    ? `Diríjase a ${turno.punto_empresa_nombre || 'Box Empresa'} para atención prioritaria`
                    : turno.tipo === 'empresa'
                    ? 'Por favor, espere a que su número sea llamado (turno empresa)'
                    : 'Por favor, espere a que su número sea llamado en la pantalla de la sala de espera'
                  }
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={imprimirTurno}
                  disabled={printing}
                  className="flex-1 bg-slate-800 hover:bg-slate-900 text-white py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-lg font-medium disabled:opacity-50"
                  data-testid="btn-imprimir"
                >
                  {printing ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Printer className="w-5 h-5" />
                  )}
                  {printing ? 'Imprimiendo...' : 'Imprimir'}
                </button>
                <button
                  onClick={resetear}
                  className={`flex-1 ${turno.tipo === 'empresa' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-500 hover:bg-blue-600'} text-white py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-lg font-medium`}
                  data-testid="btn-nuevo-turno"
                >
                  <RefreshCw className="w-5 h-5" />
                  Nuevo Turno
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-blue-200 mt-8 text-sm">
          <p>Sistema de Gestión de Turnos</p>
          {printerStatus === 'unavailable' && (
            <p className="text-xs mt-2 text-blue-300/60">
              Para impresión directa instale QZ Tray desde qz.io
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default KioskPage;
